import React, { useEffect, useState } from "react";
import { RotateCcw, RefreshCw, Ban, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DataTable, platformBadge, PLATFORM_FILTER } from "./desty/shared";

const STATUS_META = {
  detected: ["bg-amber-100 text-amber-700", "Terdeteksi Retur"],
  received_ok: ["bg-emerald-100 text-emerald-700", "Barang Diterima Sesuai"],
  received_diff: ["bg-red-100 text-red-700", "Barang Diterima Beda"],
};
const statusBadge = (s) => {
  const [cls, label] = STATUS_META[s] || ["bg-slate-200 text-slate-700", s || "-"];
  return <Badge className={`${cls} hover:${cls}`}>{label}</Badge>;
};
const STATUS_FILTER = {
  key: "status", label: "Status", allLabel: "Semua status", match: (r, v) => r.status === v,
  options: [
    { value: "detected", label: "Terdeteksi Retur" },
    { value: "received_ok", label: "Barang Diterima Sesuai" },
    { value: "received_diff", label: "Barang Diterima Beda" },
  ],
};

export default function OrderCancel() {
  const { can } = useAuth();
  const write = can("owner", "accounting", "warehouse_admin", "supervisor");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null); // {row, status, note}
  const [saving, setSaving] = useState(false);
  const [manual, setManual] = useState(null); // {query, note}
  const [savingM, setSavingM] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/desty/returns", { params: { start: start || undefined, end: end || undefined } })
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  const doExport = () => downloadFile("/desty/returns/export", { start: start || undefined, end: end || undefined }, "order_cancel.xlsx").catch(() => toast.error("Gagal export"));

  const openDlg = (row) => setDlg({ row, status: row.status || "detected", note: row.note || "" });

  const save = async () => {
    if (dlg.status === "received_ok" && dlg.row.status !== "received_ok" &&
        !window.confirm("Konfirmasi barang diterima SESUAI? Stok akan otomatis dikembalikan ke Gudang Utama.")) return;
    if (dlg.status === "received_diff" && !dlg.note.trim()) { toast.error("Isi catatan untuk 'Barang Diterima Beda'"); return; }
    setSaving(true);
    try {
      await api.put(`/desty/returns/${dlg.row.id}/status`, { status: dlg.status, note: dlg.note });
      toast.success(dlg.status === "received_ok" ? "Status diperbarui — stok dikembalikan ke Gudang Utama" : "Status retur diperbarui");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveManual = async () => {
    if (!manual.query.trim()) { toast.error("Isi No. Sales atau Order ID"); return; }
    setSavingM(true);
    try {
      await api.post("/desty/returns/manual", { query: manual.query.trim(), note: manual.note });
      toast.success("Retur manual ditambahkan");
      setManual(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingM(false); }
  };

  const cols = [
    { key: "order_sn", header: "Order ID", className: "font-mono text-xs", sortAccessor: (r) => r.order_sn, render: (r) => r.order_sn || "-" },
    { key: "platform", header: "Platform", render: (r) => platformBadge(r.platform) },
    { key: "customer_name", header: "Customer", className: "text-sm", sortAccessor: (r) => r.customer_name, render: (r) => r.customer_name || "-" },
    { key: "sale_number", header: "No. Sales", className: "text-sm", render: (r) => r.sale_number || "-" },
    { key: "total_items", header: "Item", align: "right", sortAccessor: (r) => r.total_items || 0, render: (r) => r.total_items || 0 },
    { key: "grand_total", header: "Total", align: "right", sortAccessor: (r) => r.grand_total || 0, render: (r) => fmtIDR(r.grand_total) },
    { key: "status", header: "Status", sortAccessor: (r) => r.status, render: (r) => statusBadge(r.status) },
    { key: "note", header: "Keterangan", className: "text-xs text-cocoa/70 max-w-xs", render: (r) => r.note || r.detected_reason || "-" },
    ...(write ? [{ key: "act", header: "Aksi", align: "right", render: (r) => (
      <Button size="sm" variant="outline" onClick={() => openDlg(r)} data-testid={`return-update-${r.id}`}>
        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Update
      </Button>
    ) }] : []),
  ];

  const byStatus = (s) => rows.filter((r) => r.status === s).length;

  return (
    <div data-testid="order-cancel-page">
      <PageHeader title="Order Cancel — Retur Sudah Dikasir" subtitle="Order yang dibatalkan/retur namun sudah tercatat sebagai Sales. Update status penerimaan barang; jika sesuai, stok kembali otomatis ke Gudang Utama.">
        {write && (
          <Button className="bg-cocoa hover:bg-cocoa-dark text-ivory" onClick={() => setManual({ query: "", note: "" })} data-testid="order-cancel-add-manual">
            <Plus className="w-4 h-4 mr-1.5" /> Tambah Retur Manual
          </Button>
        )}
        <Button variant="outline" onClick={doExport} data-testid="order-cancel-export">
          <Download className="w-4 h-4 mr-1.5" /> Export
        </Button>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="order-cancel-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Muat Ulang
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 mb-4 rounded-lg border border-cocoa/10 bg-white p-3" data-testid="order-cancel-date-filter">
        <div>
          <label className="text-xs text-cocoa/60 block mb-1">Dari tanggal</label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white w-44" data-testid="order-cancel-start" />
        </div>
        <div>
          <label className="text-xs text-cocoa/60 block mb-1">Sampai tanggal</label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white w-44" data-testid="order-cancel-end" />
        </div>
        {(start || end) && (
          <Button variant="ghost" size="sm" onClick={() => { setStart(""); setEnd(""); }} data-testid="order-cancel-clear-date">Reset tanggal</Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 text-xs mb-4" data-testid="order-cancel-summary">
        <span className="rounded-full bg-cocoa/10 text-cocoa px-2.5 py-1">Total: <b>{rows.length}</b></span>
        <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-1">Terdeteksi Retur: <b>{byStatus("detected")}</b></span>
        <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1">Diterima Sesuai: <b>{byStatus("received_ok")}</b></span>
        <span className="rounded-full bg-red-100 text-red-700 px-2.5 py-1">Diterima Beda: <b>{byStatus("received_diff")}</b></span>
      </div>

      <DataTable testid="order-cancel-table" columns={cols} rows={rows}
        searchAccessors={[(r) => r.order_sn, (r) => r.customer_name, (r) => r.sale_number, (r) => r.note]}
        searchPlaceholder="Cari Order ID / customer / no sales…"
        filters={[PLATFORM_FILTER, STATUS_FILTER]}
        rowTestId={(r) => `order-cancel-row-${r.id}`}
        emptyText={loading ? "Memuat…" : "Belum ada order retur/cancel yang sudah dikasir."} />

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="return-status-dialog">
          <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-2"><Ban className="w-4 h-4" /> Update Status Retur</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-taupe/10 p-3">
                <div className="font-mono text-xs">{dlg.row.order_sn}</div>
                <div className="text-cocoa/70">{dlg.row.customer_name || "-"} · Sales {dlg.row.sale_number || "-"} · {dlg.row.total_items || 0} item</div>
              </div>
              <div>
                <label className="text-xs text-cocoa/70">Status</label>
                <Select value={dlg.status} onValueChange={(v) => setDlg((d) => ({ ...d, status: v }))}>
                  <SelectTrigger className="bg-white" data-testid="return-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="detected">Terdeteksi Retur</SelectItem>
                    <SelectItem value="received_ok">Barang Diterima Sesuai (stok kembali)</SelectItem>
                    <SelectItem value="received_diff">Barang Diterima Beda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {dlg.status === "received_ok" && (
                <p className="text-xs text-emerald-700">Stok {dlg.row.total_items || 0} item akan dikembalikan ke <b>Gudang Utama</b>. Sales tetap ada & ditandai "Retur".</p>
              )}
              <div>
                <label className="text-xs text-cocoa/70">Keterangan{dlg.status === "received_diff" ? " (wajib)" : ""}</label>
                <Textarea value={dlg.note} onChange={(e) => setDlg((d) => ({ ...d, note: e.target.value }))} rows={3} className="bg-white" placeholder="Catatan penerimaan barang…" data-testid="return-note-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="return-status-save">{saving ? "Menyimpan…" : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manual} onOpenChange={(v) => !v && setManual(null)}>
        <DialogContent className="max-w-md" data-testid="manual-return-dialog">
          <DialogHeader><DialogTitle className="font-heading text-cocoa flex items-center gap-2"><Plus className="w-4 h-4" /> Tambah Retur Manual</DialogTitle></DialogHeader>
          {manual && (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-cocoa/60">Untuk barang yang balik di luar deteksi marketplace. Masukkan No. Sales atau Order ID yang sudah tercatat sebagai Sales.</p>
              <div>
                <label className="text-xs text-cocoa/70">No. Sales / Order ID</label>
                <Input value={manual.query} onChange={(e) => setManual((m) => ({ ...m, query: e.target.value }))} placeholder="mis. SLS-20260926-0013 atau 2609263HETCE6C" className="bg-white" data-testid="manual-return-query" />
              </div>
              <div>
                <label className="text-xs text-cocoa/70">Keterangan</label>
                <Textarea value={manual.note} onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))} rows={3} className="bg-white" placeholder="Alasan retur…" data-testid="manual-return-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManual(null)}>Batal</Button>
            <Button onClick={saveManual} disabled={savingM} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="manual-return-save">{savingM ? "Menyimpan…" : "Tambah"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
